import type { PiiSpan } from "./types";
import PLACE_NAMES from "./ruleset/placeNames.data.json";

/**
 * Layer 2b of the engine (LAR-367 #5b): suppress bare country/city/region names.
 *
 * DAK does not consider a place name personal data on its own — "min klient bor
 * i København" should send as written — but the same name inside an address
 * ("Nørregade 12, 8000 Aarhus") must stay flagged. ScandiNER cannot tell the two
 * apart: it emits LOC for streets, towns and countries alike (verified against
 * the model), so the distinction has to be made here.
 *
 * The rule is deliberately asymmetric, so that the failure mode is
 * over-redaction rather than a leak:
 *
 *  1. Only a span whose surface is IN the curated place-name list is a candidate
 *     for dropping. Anything else — every street name, every unknown place —
 *     behaves exactly as before, so an incomplete list costs nothing but noise.
 *  2. A candidate is kept anyway when it sits in address context: a Danish
 *     postal code immediately in front of it ("8000 Aarhus"), or an adjacent
 *     address span that is itself kept, separated by nothing but address glue
 *     ("Nørregade 12, 8000 Aarhus, Danmark" keeps all three).
 *
 * Applies to NER spans only — `detect()` passes them in before the regex hard
 * identifiers are merged, so a CPR or a user's own manual highlight can never be
 * suppressed by a place name.
 */

/** Danish postal district suffixes ("København K", "Odense SØ"). */
const DISTRICT_SUFFIX = /\s+(?:c|k|m|n|s|v|ø|nv|sv|sø|nø)$/;

/** Words that may sit between two parts of one address without breaking it. */
const GLUE_WORDS = new Set(["i", "på", "ved", "nær", "dk", "by", "og"]);

/**
 * Separators allowed inside one address, spelled out on purpose. `\s` and `\b`
 * are NOT interchangeable between the two engines — JS `\s` matches U+FEFF while
 * Python's does not, and Python's `\b` is Unicode-aware so it fails between "ø"
 * and a digit — and either difference would let the Python worker keep a span
 * the browser drops, or the reverse. An explicit class behaves identically.
 */
const SEPARATORS = " \\t\\r\\n\\u00A0\\u2007\\u2009\\u202F\\uFEFF,.\\-";

/**
 * Postal code right before the span → the span is its postal town. The
 * digit-only lookbehind (rather than `\b`) also covers "DK-8000" and "DK8000".
 */
const POSTAL_CODE_BEFORE = new RegExp(`(?<!\\d)\\d{4}[${SEPARATORS}]*$`);

/** Gap between two address parts, split into words. */
const GLUE_SPLIT = new RegExp(`[${SEPARATORS}/]+`);

/** How far apart two spans may sit and still belong to one address. */
const MAX_GLUE_CHARS = 12;

function normalizePlace(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

const DROP_LIST: ReadonlySet<string> = new Set(
  [
    ...PLACE_NAMES.countries,
    ...PLACE_NAMES.regions,
    ...PLACE_NAMES.postalTowns,
  ].map(normalizePlace)
);

/** True when the surface is a known bare place name ("København K" → København). */
export function isKnownPlaceName(text: string): boolean {
  const normalized = normalizePlace(text);
  if (DROP_LIST.has(normalized)) return true;
  const withoutDistrict = normalized.replace(DISTRICT_SUFFIX, "");
  return withoutDistrict !== normalized && DROP_LIST.has(withoutDistrict);
}

/** True when nothing but address glue separates the two offsets. */
function isAddressGlue(gap: string): boolean {
  if (gap.length > MAX_GLUE_CHARS) return false;
  const words = gap.toLowerCase().split(GLUE_SPLIT).filter(Boolean);
  return words.every((word) => GLUE_WORDS.has(word) || /^\d{4}$/.test(word));
}

/**
 * Drop `address` spans that are bare place names outside any address context.
 * Returns a new array; other span types pass through untouched.
 */
export function dropStandalonePlaceNames(
  text: string,
  spans: readonly PiiSpan[]
): PiiSpan[] {
  const addresses = spans
    .map((span, index) => ({ span, index }))
    .filter(({ span }) => span.type === "address")
    .sort((a, b) => a.span.start - b.span.start);

  // Only model output is a candidate: a span the USER classified as an address
  // stands, and it still counts as context for its neighbours below.
  const candidates = addresses.filter(
    ({ span }) => span.source === "ner" && isKnownPlaceName(span.text)
  );
  if (candidates.length === 0) return [...spans];

  // A span is kept unless it is a candidate; a candidate is kept once address
  // context is found for it. Chains resolve by repeating the sweep — "8000
  // Aarhus, Danmark" keeps Aarhus on the postal code, then Danmark on Aarhus.
  const dropped = new Set(candidates.map(({ index }) => index));
  const keptSomething = (position: number): boolean => {
    const entry = addresses[position];
    return entry !== undefined && !dropped.has(entry.index);
  };

  let changed = true;
  while (changed) {
    changed = false;
    addresses.forEach(({ span, index }, position) => {
      if (!dropped.has(index)) return;

      const before = text.slice(0, span.start);
      let keep = POSTAL_CODE_BEFORE.test(before);

      if (!keep && keptSomething(position - 1)) {
        const previous = addresses[position - 1].span;
        keep = isAddressGlue(text.slice(previous.end, span.start));
      }
      if (!keep && keptSomething(position + 1)) {
        const next = addresses[position + 1].span;
        keep = isAddressGlue(text.slice(span.end, next.start));
      }

      if (keep) {
        dropped.delete(index);
        changed = true;
      }
    });
  }

  return spans.filter((_, index) => !dropped.has(index));
}
